import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { History, Calendar, FileText, MapPin, User } from 'lucide-react';
import { Separator } from './ui/separator';

const HistoryModal = ({ isOpen, onClose, instrument }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });
    } catch (error) {
      return 'N/A';
    }
  };

  // Função auxiliar para criar data válida
  const createSafeDate = (baseDate, daysOffset = 0) => {
    try {
      // Usar proximaCalibracao como base ou data atual
      const base = baseDate || instrument.proximaCalibracao || new Date().toISOString().split('T')[0];
      const date = new Date(base);
      
      if (isNaN(date.getTime())) {
        // Se a data for inválida, usar data atual
        return new Date().toISOString().split('T')[0];
      }
      
      if (daysOffset !== 0) {
        date.setDate(date.getDate() + daysOffset);
      }
      
      return date.toISOString().split('T')[0];
    } catch (error) {
      return new Date().toISOString().split('T')[0];
    }
  };

  // Mock history events
  const historyEvents = [
    {
      id: 1,
      type: 'calibracao',
      date: createSafeDate(instrument.proximaCalibracao, -30),
      description: 'Calibração realizada',
      user: instrument.responsavel || 'Sistema',
      details: instrument.orcamentoNumero ? `Orçamento: ${instrument.orcamentoNumero}` : 'Aguardando informações',
    },
    {
      id: 2,
      type: 'atribuicao',
      date: createSafeDate(instrument.proximaCalibracao, -60),
      description: 'Instrumento atribuído',
      user: 'Sistema',
      details: `Responsável: ${instrument.responsavel || 'Não atribuído'}`,
    },
    {
      id: 3,
      type: 'cadastro',
      date: createSafeDate(instrument.proximaCalibracao, -90),
      description: 'Instrumento cadastrado no sistema',
      user: 'Administrador',
      details: `Setor: ${instrument.setor || 'Não especificado'}`,
    },
  ];

  const getEventIcon = (type) => {
    switch (type) {
      case 'calibracao':
        return <Calendar className="w-5 h-5 text-blue-600" />;
      case 'atribuicao':
        return <User className="w-5 h-5 text-green-600" />;
      case 'cadastro':
        return <FileText className="w-5 h-5 text-purple-600" />;
      default:
        return <History className="w-5 h-5 text-slate-600" />;
    }
  };

  const getEventBadge = (type) => {
    switch (type) {
      case 'calibracao':
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Calibração</Badge>;
      case 'atribuicao':
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Atribuição</Badge>;
      case 'cadastro':
        return <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">Cadastro</Badge>;
      default:
        return <Badge>Evento</Badge>;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <History className="w-6 h-6 text-purple-600" />
            Histórico do Instrumento
          </DialogTitle>
          <DialogDescription>Visualize o histórico completo de eventos</DialogDescription>
        </DialogHeader>

        {/* Instrument Info Card */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
          <h3 className="font-bold text-lg text-slate-900 mb-3">{instrument.nome}</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-600" />
              <span className="text-slate-700">
                <strong>Marca:</strong> {instrument.marca}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-600" />
              <span className="text-slate-700">
                <strong>Tipo:</strong> {instrument.tipo}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-slate-600" />
              <span className="text-slate-700">
                <strong>Setor:</strong> {instrument.setor || 'N/A'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-slate-600" />
              <span className="text-slate-700">
                <strong>Responsável:</strong> {instrument.responsavel}
              </span>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="space-y-4 mt-6">
          <h4 className="font-semibold text-slate-900 text-lg">Linha do Tempo</h4>
          <div className="space-y-4">
            {historyEvents.map((event, index) => (
              <div key={event.id}>
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center shadow-sm">
                      {getEventIcon(event.type)}
                    </div>
                    {index < historyEvents.length - 1 && (
                      <div className="w-0.5 h-full bg-slate-200 mt-2" style={{ minHeight: '40px' }} />
                    )}
                  </div>
                  <div className="flex-1 pb-6">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h5 className="font-semibold text-slate-900">{event.description}</h5>
                        <p className="text-sm text-slate-600 mt-1">{formatDate(event.date)}</p>
                      </div>
                      {getEventBadge(event.type)}
                    </div>
                    <p className="text-sm text-slate-700 mt-2">{event.details}</p>
                    <p className="text-xs text-slate-500 mt-1">Por: {event.user}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Separator className="my-4" />

        {/* Upcoming Events */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <h4 className="font-semibold text-orange-900 mb-2 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Próximos Eventos
          </h4>
          <p className="text-sm text-orange-800">
            Próxima calibração agendada para: <strong>{formatDate(instrument.proximaCalibracao)}</strong>
          </p>
        </div>

        <div className="flex justify-end pt-4">
          <Button onClick={onClose} className="bg-slate-900 hover:bg-slate-800">
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HistoryModal;